// src/components/ReportQuestionDialog.tsx
'use client'

import { useState } from 'react'
import { Flag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import {
  REPORT_REASON_LABELS,
  VALID_REPORT_REASONS,
  type ReportReason,
} from '@/lib/question-bank/question-reports'

/** Where the student reads the outcome (/settings/reports), in words. */
export const MY_REPORTS_LOCATION = 'Settings → Question reports'

type Props = {
  questionId: string
  /** Optional label override; defaults to icon-only for compact placement. */
  label?: string
  /** "icon" (default) or "button" — controls trigger size/styling. */
  size?: 'icon' | 'button'
  className?: string
}

export default function ReportQuestionDialog({
  questionId,
  label,
  size = 'icon',
  className,
}: Props) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setReason('')
    setDetails('')
  }

  const handleSubmit = async () => {
    if (!reason) {
      toast({
        title: 'Choose a reason',
        description: "Please pick what's wrong with this question.",
      })
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/api/reports/question', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          questionId,
          reason,
          details: details.trim() || undefined,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error ?? 'Failed to submit report')
      }

      // Text, not a link: this dialog also opens mid-test, where a link out of
      // the runner would cost the student their place.
      toast({
        title: 'Report submitted',
        description: `Thanks — we'll review it and reply on your dashboard. Track it anytime in ${MY_REPORTS_LOCATION}.`,
      })
      reset()
      setOpen(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit report'
      toast({ title: 'Could not submit report', description: msg })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <DialogTrigger asChild>
        {size === 'button' ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={className}
            aria-label="Report issue with this question"
          >
            <Flag className="mr-2 h-4 w-4" aria-hidden="true" />
            {label ?? 'Report issue'}
          </Button>
        ) : (
          <button
            type="button"
            aria-label="Report issue with this question"
            title="Report issue"
            className={
              'inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card/60 text-muted-foreground transition-colors hover:border-red-500/60 hover:bg-red-500/10 hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ' +
              (className ?? '')
            }
          >
            <Flag className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{label ?? 'Report issue'}</span>
          </button>
        )}
      </DialogTrigger>

      <DialogContent className="border-border bg-background text-foreground sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Report this question</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Spotted something wrong? Let us know and we'll take a look — every report gets an answer
            in {MY_REPORTS_LOCATION}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="report-reason" className="block text-sm font-medium text-foreground">
              What's the issue?
            </label>
            <Select value={reason} onValueChange={(v) => setReason(v as ReportReason)}>
              <SelectTrigger id="report-reason" className="border-border bg-card text-foreground">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent className="border-border bg-card text-foreground">
                {VALID_REPORT_REASONS.map((r) => (
                  <SelectItem
                    key={r}
                    value={r}
                    className="text-foreground focus:bg-muted focus:text-foreground"
                  >
                    {REPORT_REASON_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="report-details" className="block text-sm font-medium text-foreground">
              Additional details{' '}
              <span className="text-xs font-normal text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Tell us what looks off…"
              maxLength={2000}
              className="min-h-[96px] border-border bg-card text-foreground placeholder:text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground">{details.length} / 2000</p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
